import {
  ArrowDownTrayIcon,
  ArrowUturnLeftIcon,
  MagnifyingGlassIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { feeSplitSummary } from 'components/FeeSplits'
import { LayoutLoading } from 'components/LayoutLoading'
import { sumBy, TotalsRow } from 'components/TableTotals'
import { itemCountPerPage } from 'config'
import { useSharedLoan } from 'context/SharedLoanContext'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  addNewLoanCharge,
  deleteLoanCharge,
  getLoanChargeCategories,
  getLoanCharges,
  getLoanChargeSummary,
  getUsedLoanChargeCategories,
  openS3Document,
  unwaiveLoanCharge,
  updateLoanCharge,
  waiveLoanCharge,
} from 'services'
import { Button, ButtonGroup, Input2, Pagination, Select } from 'stories/components'
import { confirm, formatDate, getLoanDisplayNo, getPrice2decimal, renderHeader } from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

import { usePayoffGuard } from '../payoffGuard'
import { AddLoanCharge } from './AddLoanCharge'
import { ChargesSummary } from './ChargesSummary'
import {
  type ILoanCharge,
  type ILoanChargeCategory,
  type ILoanChargeSummary,
  canWaiveCharge,
  isChargeWaived,
  LoanChargeGroupBy,
  LoanChargeOwedBy,
  LoanChargePaidStatus,
  LoanChargeType,
  UNCATEGORIZED,
} from './types'
import { WaiveLoanCharge } from './WaiveLoanCharge'

// Right-aligned numeric columns in the loan-charges grid.
const NUMERIC_KEYS = ['interestRate', 'originalBalance', 'unpaidBalance', 'accruedInterest', 'totalDue', 'waivedAmount']

const isEmpty = (v: any) => v === null || v === undefined || v === ''
// Currency cell: "$1,234.56" (2 decimals, thousands separators, $), blank for empty.
const money = (v: any) => (isEmpty(v) ? '' : getPrice2decimal(v, false, true, true))
// Interest-rate cell: "12.50%", blank for empty.
const pct = (v: any) => (isEmpty(v) ? '' : `${getPrice2decimal(v, false, true)}%`)

/**
 * Whether a charge is settled, matching the server's `paidStatus` filter: the flag OR an exhausted
 * balance. Both halves are needed - a payoff marks a loan's charges paid wholesale without zeroing
 * them, so the flag alone and the balance alone each miss cases. A waived charge is settled too -
 * nothing is left to collect - but it was forgiven rather than paid, so it is labelled as such.
 */
const isSettled = (item: ILoanCharge) => !!item.isPaid || Number(item.unpaidBalance) <= 0

// The two views of a charges section: the charges themselves, or the same charges totalled one
// line per kind of fee.
const DETAIL = 'Detail'
const SUMMARY = 'Summary'
const VIEWS = [DETAIL, SUMMARY]

// What the summary buckets on. Description is the charge's own label, which is what a converted
// book carries; category is the fee category it was filed under.
const GROUP_BY_TITLES: Record<string, string> = {
  [LoanChargeGroupBy.Description]: 'Charge Type',
  [LoanChargeGroupBy.Category]: 'Category',
}

export const LoanChargesTable = ({
  isLoan = true,
  loanChargeType = LoanChargeType.LoanCharges,
  owedBy = LoanChargeOwedBy.Borrower,
}: {
  isLoan?: any
  loanChargeType?: LoanChargeType
  /**
   * Which side of the book this table lists. Borrower and lender charges are kept in separate
   * tables rather than one mixed grid because they are not the same money - a borrower charge is
   * collected from the borrower, a lender charge is netted off an investor's distribution - so a
   * single "Total Amount" across both would mean nothing.
   */
  owedBy?: LoanChargeOwedBy
}) => {
  const shared = useSharedLoan()
  const isShared = !!shared?.readOnly
  const isLenderTable = owedBy === LoanChargeOwedBy.Lender
  // Warns before entering a charge against an active payoff.
  const confirmPayoff = usePayoffGuard()
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [data, setData] = useState<ILoanCharge[]>([])
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [editingData, setEditingData] = useState<ILoanCharge | null>(null)
  // The charge the waive modal is open on, if any.
  const [waivingItem, setWaivingItem] = useState<ILoanCharge | null>(null)
  const [query, setQuery] = useState('')
  const [isGetUsersOnce, setIsGetUsersOnce] = useState(false)
  const [totalAmount, setTotalAmount] = useState(0)
  // Column totals over the whole filtered set, summed server-side - the cross-loan view is
  // paginated, so totalling the rows in hand would quietly cover one page.
  const [totals, setTotals] = useState<Record<string, number> | null>(null)
  const [filters, setFilters] = useState({
    orderBy: 'createdAt',
    orderDir: '-1',
    isLoan: isLoan,
    pageNum: 0,
    count: itemCountPerPage,
    type: loanChargeType,
    category: '',
    // '' lists settled and unsettled charges together, which is what the grid always did.
    paidStatus: '' as '' | LoanChargePaidStatus,
    owedBy,
  })
  const [totalCount, setTotalCount] = useState(0)
  const [categoryOptions, setCategoryOptions] = useState<string[]>([])
  // Whether these rows are actually filed across categories, which is NOT the same question as
  // what the filter offers: the options include categories the tenant configured but has not used
  // yet. Grouping the summary on a category nothing carries yields a single line, so the Group by
  // picker follows this and not the option list.
  const [isCategorized, setIsCategorized] = useState(false)
  const [view, setView] = useState(DETAIL)
  const [groupBy, setGroupBy] = useState<LoanChargeGroupBy>(LoanChargeGroupBy.Description)
  // Once the grouping has been picked by hand it is left alone, so a late-arriving category list
  // cannot move the view out from under whoever is reading it.
  const groupByPicked = useRef(false)
  const [summary, setSummary] = useState<ILoanChargeSummary | null>(null)

  useEffect(() => {
    if (isShared) return
    setLoanNumber()
  }, [isShared])

  /**
   * The filter offers every category the tenant has configured, plus any the charges on screen
   * carry that the picker no longer lists - a retired category still has history behind it and has
   * to stay selectable. A configured category is offered whether or not these rows use one yet,
   * because the list is curated precisely so charges can be filed against it and an uncategorized
   * book is the normal starting point: offering only what was already in use hid the control
   * outright on a converted book, where every charge is blank. A category nothing is filed under
   * yet therefore filters to an empty grid, which is the honest answer to "what is in Taxes".
   *
   * The used list is still fetched, scoped to this grid (isLoan / type / owedBy), for two things it
   * alone can say: which retired categories to keep selectable, and whether these rows are
   * categorized at all.
   */
  useEffect(() => {
    if (isShared) return
    Promise.all([getLoanChargeCategories(true), getUsedLoanChargeCategories({ isLoan, type: loanChargeType, owedBy })])
      .then(([configured, used]: [ILoanChargeCategory[], string[]]) => {
        const usedValues = used || []
        // No charges in this scope at all - the server answers with an empty list, and a filter
        // over nothing is noise. Leaving the options empty keeps the control hidden.
        if (!usedValues.length) {
          setCategoryOptions([])
          setIsCategorized(false)
          return
        }
        const configuredValues = (configured || []).map((row) => row.value).filter(Boolean)
        // Retired: filed against these rows but gone from the picker, so the configured list misses
        // them. Alphabetical, after the curated ones, which keep the tenant's own sort order.
        const retired = usedValues
          .filter((value) => value && value !== UNCATEGORIZED && !configuredValues.includes(value))
          .sort((a, b) => a.localeCompare(b))
        const named = [...configuredValues, ...retired]
        // Deduped because the options double as their own React keys, and a tenant is free to
        // configure a category named 'Uncategorized' that would collide with the bucket below it.
        const offered = usedValues.includes(UNCATEGORIZED) ? [...named, UNCATEGORIZED] : named
        setCategoryOptions([...new Set(offered)])

        // A categorized book is summarised by category by default: it is the grouping that
        // answers "what do we hold in force-placed insurance", where the description would split
        // that into one line per policy period. An uncategorized book has nothing to group on,
        // so it keeps the description. This reads the USED list, not the offered one - a configured
        // category no charge carries yet would otherwise group the whole book into one line.
        const usedNamed = usedValues.filter((value) => value && value !== UNCATEGORIZED)
        setIsCategorized(usedNamed.length > 1)
        if (!groupByPicked.current && usedNamed.length > 1) setGroupBy(LoanChargeGroupBy.Category)
      })
      .catch(() => {})
  }, [isShared, isLoan, loanChargeType, owedBy])

  useEffect(() => {
    if (isShared || !isGetUsersOnce) return
    const timeOutId = setTimeout(() => !loading && refetch(), 700)
    return () => clearTimeout(timeOutId)
  }, [query])

  useEffect(() => {
    if (isShared) {
      const pageData = shared.pageData as any
      const key =
        loanChargeType === LoanChargeType.UnpaidInterest
          ? 'unpaidInterest'
          : loanChargeType === LoanChargeType.UnpaidDefaultInterest
          ? 'unpaidDefaultInterest'
          : loanChargeType === LoanChargeType.UnpaidLateCharges
          ? 'unpaidLateCharges'
          : 'loanCharges'
      const section = pageData?.[key] || {}
      setData(section.results || [])
      setTotalCount(section.totalCount || 0)
      setTotalAmount(section.totalAmount || 0)
      const rows: ILoanCharge[] = section.results || []
      setTotals(
        section.totals || {
          originalBalance: sumBy(rows, 'originalBalance'),
          unpaidBalance: sumBy(rows, 'unpaidBalance'),
          accruedInterest: sumBy(rows, 'accruedInterest'),
          totalDue: sumBy(rows, 'totalDue'),
        },
      )
      setIsGetUsersOnce(true)
      setLoading(false)
      return
    }
    refetch()
  }, [filters, isShared, shared?.pageKey, shared?.pageData, loanChargeType])

  /**
   * The summary over exactly the charges the grid is showing - same scoping, same category and
   * status filters, same search - so switching views never changes which charges the figures
   * cover.
   */
  const refetchSummary = (applied: Record<string, any> = filters) => {
    const { isLoan: isLoanScope, type, category, paidStatus, owedBy: owedByScope } = applied
    return getLoanChargeSummary({
      isLoan: isLoanScope,
      type,
      category,
      paidStatus,
      owedBy: owedByScope,
      query,
      groupBy,
    })
      .then((data) => setSummary(data))
      .catch(() => setSummary(null))
  }

  const refetch = (newFilters: Record<string, any> | null = null) => {
    if (isShared) return
    setLoading(true)
    const applied = newFilters || filters
    Promise.all([
      getLoanCharges({ ...applied, query }).then((data) => {
        setIsGetUsersOnce(true)
        setData(data.results)
        setTotalCount(data.totalCount)
        setTotalAmount(data.totalAmount)
        setTotals(data.totals || null)
      }),
      // Only while that view is on screen; nothing reads it otherwise.
      view === SUMMARY ? refetchSummary(applied) : Promise.resolve(),
    ]).finally(() => setLoading(false))
  }

  // Opening the summary, or regrouping it, fetches it on its own: the grid behind it has not
  // changed, so there is nothing to refetch there.
  useEffect(() => {
    if (isShared || view !== SUMMARY) return
    setLoading(true)
    refetchSummary().finally(() => setLoading(false))
  }, [isShared, view, groupBy])

  const onAdd = async () => {
    if (isShared) return
    if (!(await confirmPayoff())) return
    setEditingData(null)
    setIsOpen(true)
    setSelectedIndex(-1)
  }

  const onCloseModal = (isNeedRefetch: boolean) => {
    setIsOpen(false)
    setEditingData(null)
    if (isNeedRefetch) refetch()
  }

  const onEdit = (id: number, index: number) => {
    setEditingData(data[index])
    setIsOpen(true)
    setSelectedIndex(index)
  }

  const onSubmit = async (newData: Record<string, any>) => {
    setLoading(true)
    if (selectedIndex == -1) {
      await addNewLoanCharge({ ...newData, type: loanChargeType })
    } else {
      const item = data[selectedIndex]
      await updateLoanCharge(item.id, newData)
    }
    setIsOpen(false)
    setSelectedIndex(-1)
    refetch()
  }

  const onDelete = async (id: number, index: number) => {
    const content = (
      <div className="mb-4 text-[18px]">
        Do you want to remove this item?
        <br />
        No. {index + 1}
      </div>
    )
    const result = await confirm(content)
    if (!result) return
    setLoading(true)
    await deleteLoanCharge(id)
    refetch()
  }

  /**
   * Waive a charge: forgive what is owed on it instead of collecting it, in full or in part, and
   * keep the charge on the loan as the record that the fee was charged and then waived. A full
   * waiver stops the charge being billed, collected or quoted; a partial one takes the amount off
   * the balance and the rest keeps billing. The reason is optional - the amount, the date and the
   * user are the trail that matters.
   */
  const onWaiveSubmit = async (item: ILoanCharge, data: { amount?: number; reason: string }) => {
    setWaivingItem(null)
    setLoading(true)
    try {
      const res: any = await waiveLoanCharge(item.id, data)
      if (res?.success)
        toast(
          res.partial
            ? `Waived $${getPrice2decimal(res.waivedAmount, false, true)}. $${getPrice2decimal(
                res.remaining,
                false,
                true,
              )} is still owed on the charge.`
            : `Waived $${getPrice2decimal(res.waivedAmount, false, true)}.`,
          { type: 'success' },
        )
      else toast(res?.error || 'The charge could not be waived.', { type: 'error' })
    } catch {
      toast('The charge could not be waived.', { type: 'error' })
    }
    refetch()
  }

  /** Reverse a waiver, putting back everything it forgave so the charge bills again. */
  const onUnwaive = async (item: ILoanCharge) => {
    const content = (
      <div className="mb-4 text-[18px]">
        Reverse the waiver on this charge?
        <br />
        <span className="text-[14px] text-gray-500">
          ${getPrice2decimal(Number(item.waivedAmount || 0), false, true)} goes back on the charge and bills again
          {!item.waived && ', on top of what it still owes'}.
        </span>
      </div>
    )
    if (!(await confirm(content))) return
    setLoading(true)
    try {
      const res: any = await unwaiveLoanCharge(item.id)
      if (res?.success) toast('The waiver was reversed.', { type: 'success' })
      else toast(res?.error || 'The waiver could not be reversed.', { type: 'error' })
    } catch {
      toast('The waiver could not be reversed.', { type: 'error' })
    }
    refetch()
  }

  const onSort = (key: string, dir: number) => {
    const newFilters = cloneDeep(filters)
    newFilters['orderBy'] = key
    newFilters['orderDir'] = `${dir}`
    setFilters(newFilters)
  }

  const onPageNavigate = (num: number) => {
    const newFilters = cloneDeep(filters)
    newFilters.pageNum = num
    setFilters(newFilters)
  }

  const onCategoryFilter = (value: string) => {
    const newFilters = cloneDeep(filters)
    newFilters.category = value
    // Back to the first page: the previous page number rarely exists in the narrowed result set.
    newFilters.pageNum = 0
    setFilters(newFilters)
  }

  const onPaidStatusFilter = (value: string) => {
    const newFilters = cloneDeep(filters)
    newFilters.paidStatus = value as '' | LoanChargePaidStatus
    newFilters.pageNum = 0
    setFilters(newFilters)
  }

  const sortableHeaders = [
    { title: 'Loan Number', key: 'loanNumber' },
    { title: 'Date', key: 'date' },
    { title: 'Category', key: 'category' },
    { title: 'Description', key: 'description' },
    { title: 'Interest Rate', key: 'interestRate' },
    { title: 'Interest From Date', key: 'interestFromDate' },
    // Deferred is a borrower idea (roll it into what they owe later). A lender charge is recovered
    // from a distribution instead, so that column gives way to who owes it and whether it still
    // stands.
    ...(isLenderTable
      ? [
          { title: 'Lender', key: 'lenderName' },
          { title: 'Status', key: 'isPaid' },
        ]
      : [{ title: 'Deferred', key: 'deferred' }]),
    { title: 'Original Balance', key: 'originalBalance' },
    { title: 'Unpaid Balance', key: 'unpaidBalance' },
    { title: 'Accrued Interest', key: 'accruedInterest' },
    { title: 'Total Due', key: 'totalDue' },
    { title: 'Waived', key: 'waivedAmount' },
  ]

  return (
    <div className={`relative bg-white`}>
      <h2 className="text-2xl font-bold flex items-center mb-5">
        {isLenderTable
          ? 'Lender Charges'
          : loanChargeType === LoanChargeType.LoanCharges
          ? 'Loan Charges'
          : loanChargeType === LoanChargeType.UnpaidInterest
          ? 'Unpaid Interest'
          : loanChargeType === LoanChargeType.UnpaidDefaultInterest
          ? 'Unpaid Default Interest'
          : 'Unpaid Late Charges'}
      </h2>
      <LayoutLoading show={loading} />
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <p>
            {/* `|| 0` because an empty result answers `totalAmount: null`, which the formatter
                renders as nothing at all - a bare "$" that reads as a broken screen. Filtering to a
                category nothing is filed under yet is an ordinary outcome, so it has to say $0.00. */}
            Total Amount: <span className="bold">${getPrice2decimal(totalAmount || 0, false, true)}</span>
          </p>
          {/* The shared (borrower) view is served a fixed snapshot of the rows and never calls the
              API, so there is nothing to summarise there. */}
          {!isShared && <ButtonGroup title={VIEWS} value={view} onChange={setView} />}
        </div>
        <div className="flex justify-end items-end gap-3">
          {/* Settled charges in or out. A servicer working the charges on a loan is chasing what is
              still owed, and a long-serviced loan's grid is mostly history - but the paid rows are
              the audit trail of what was collected, so they are filtered out on request rather
              than hidden by default. The totals follow the filter, so "Unpaid" turns Total Amount
              into what is outstanding. Not offered on the shared (borrower) view, which is served a
              fixed snapshot and never refetches. */}
          {!isShared && (
            <div className="w-48">
              <Select
                id={`charge-paid-filter-${owedBy}-${loanChargeType}`}
                title="Status"
                options={{
                  [LoanChargePaidStatus.Unpaid]: isLenderTable ? 'Outstanding' : 'Unpaid',
                  [LoanChargePaidStatus.Paid]: isLenderTable ? 'Recovered' : 'Paid',
                }}
                hasDefaultOption
                defaultOptionText="All charges"
                value={filters.paidStatus}
                onChange={onPaidStatusFilter}
              />
            </div>
          )}
          {/* What the buckets are keyed on. Category is only worth offering once these rows
              actually split across categories - an imported book where every charge is
              uncategorized would group into a single line. Deliberately NOT the filter's option
              list, which also offers categories the tenant has configured but not yet used. */}
          {view === SUMMARY && isCategorized && (
            <div className="w-56">
              <Select
                id={`charge-summary-group-by-${owedBy}-${loanChargeType}`}
                title="Group by"
                options={GROUP_BY_TITLES}
                value={groupBy}
                onChange={(value: string) => {
                  groupByPicked.current = true
                  setGroupBy(value as LoanChargeGroupBy)
                }}
              />
            </div>
          )}
          {/* Every category the tenant has configured, plus any retired one these rows still carry,
              and Uncategorized when some of them have no category. The shared (borrower) view is
              served a fixed snapshot and never refetches, so a filter there would do nothing, and
              with a single option there is nothing to narrow - one category and nothing else means
              every row already answers it. */}
          {!isShared && categoryOptions.length > 1 && (
            <div className="w-56">
              <Select
                id={`charge-category-filter-${owedBy}-${loanChargeType}`}
                title="Category"
                options={categoryOptions}
                hasDefaultOption
                defaultOptionText="All categories"
                value={filters.category}
                onChange={onCategoryFilter}
              />
            </div>
          )}
          {isLoan ? (
            !isShared && (
              <Button onClick={onAdd} className="px-[36px]">
                Add
              </Button>
            )
          ) : (
            <div className="w-72">
              <Input2
                type="search"
                title="Search"
                hasIcon
                icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />}
                value={query}
                onChange={(value) => setQuery(value)}
              />
            </div>
          )}
        </div>
      </div>
      {view === SUMMARY && <ChargesSummary summary={summary} groupBy={groupBy} />}
      {/* The grid is hidden rather than unmounted while the summary is up, so its page, sort and
          scroll position are still there when the view is switched back. */}
      <div
        className={`parties-container overflow-auto max-h-[70vh] mb-6 mt-6 shadow-md sm:rounded-lg ${
          view === SUMMARY ? 'hidden' : ''
        }`}
      >
        <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-6 py-3 sticky top-0 z-20 bg-slate-50 dark:bg-gray-700">
                No
              </th>
              {sortableHeaders
                .filter((item) => !isLoan || item.key !== 'loanNumber')
                .map(({ title, key }, index) =>
                  renderHeader({
                    className: 'sticky top-0 z-20 bg-slate-50 dark:bg-gray-700',
                    title,
                    sortable: true,
                    key,
                    sortOrder: filters.orderBy == key ? parseInt(filters.orderDir) : 0,
                    index,
                    onSort: (key: string, dir: number) => onSort(key, dir),
                    alignClassName: NUMERIC_KEYS.includes(key) ? 'justify-end' : '',
                  }),
                )}
              <th scope="col" className="px-6 py-3 sticky top-0 z-20 bg-slate-50 dark:bg-gray-700">
                Files
              </th>
              {!isShared && (
                <th scope="col" className="px-6 py-3 sticky top-0 z-20 bg-slate-50 dark:bg-gray-700">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="text-[14.5px] text-gray-900">
            {data.map((item, index) => {
              return (
                <tr
                  key={index}
                  // A waived charge is history, not money owed: it is muted so the rows that still
                  // bill stand out from the ones that no longer do.
                  className={`border-b ${index % 2 ? 'bg-gray-50' : ''} ${item.waived ? 'text-gray-400' : ''}`}
                >
                  <td className="px-6 py-3">{index + 1 + itemCountPerPage * filters.pageNum}</td>
                  {!isLoan && <td className="px-2 py-3">{getLoanDisplayNo(item)}</td>}
                  <td className="px-2 py-3 whitespace-nowrap">{formatDate(item.date)}</td>
                  <td className="px-2 py-3 whitespace-nowrap">
                    {item.category || ''}
                    {/* What the fee pays out to somebody other than the investors when it is
                        collected, so a split charge is recognisable without opening it. */}
                    {!isLenderTable && feeSplitSummary(item.splits) && (
                      <p className="text-[11px] text-gray-500">{feeSplitSummary(item.splits)}</p>
                    )}
                  </td>
                  <td className="px-2 py-3">
                    {item.description}
                    {/* So a paid charge is recognisable with the Status filter left on "All" - the
                        lender table has a Status column of its own instead. A waived charge is
                        settled as well, but it carries its own waiver columns and a muted row, so
                        it is not labelled paid on top of that. */}
                    {!isLenderTable && !item.waived && isSettled(item) && (
                      <span className="ml-2 text-[11px] text-green-700 whitespace-nowrap">Paid</span>
                    )}
                  </td>
                  <td className="px-2 py-3 text-right tabular-nums">{pct(item.interestRate)}</td>
                  <td className="px-2 py-3 whitespace-nowrap">{formatDate(item.interestFromDate)}</td>
                  {isLenderTable ? (
                    <>
                      <td className="px-2 py-3">{item.lenderName || ''}</td>
                      <td className="px-2 py-3 whitespace-nowrap">
                        {item.isPaid ? (
                          <span className="text-green-700">Recovered</span>
                        ) : (
                          <span className="text-gray-600">Outstanding</span>
                        )}
                      </td>
                    </>
                  ) : (
                    <td className="px-2 py-3">
                      {item.deferred ? 'Yes' : 'No'}
                      {/* Why it is being carried rather than collected, the same trail the waiver
                          keeps. Only a deferred charge has one - the server clears it with the
                          deferral. */}
                      {item.deferred && item.deferredReason && (
                        <p className="text-[11px] text-gray-500 max-w-[180px] whitespace-normal">
                          {item.deferredReason}
                        </p>
                      )}
                    </td>
                  )}
                  <td className="px-2 py-3 text-right tabular-nums">{money(item.originalBalance)}</td>
                  <td className="px-2 py-3 text-right tabular-nums">{money(item.unpaidBalance)}</td>
                  <td className="px-2 py-3 text-right tabular-nums">{money(item.accruedInterest)}</td>
                  <td className="px-2 py-3 text-right tabular-nums">{money(item.totalDue)}</td>
                  {/* The waiver itself: what was forgiven, when, by whom and why - the trail that
                      keeping the charge instead of deleting it is for. Shown on a partial waiver
                      too, where the charge is not flagged waived because it still bills the rest. */}
                  <td className="px-2 py-3 text-right tabular-nums whitespace-nowrap">
                    {isChargeWaived(item) ? (
                      <>
                        <span className="text-amber-700 font-medium">{money(item.waivedAmount)}</span>
                        {!item.waived && <span className="text-[11px] text-gray-500"> partial</span>}
                        <p className="text-[11px] text-gray-500">
                          {item.waivedDate ? formatDate(item.waivedDate) : ''}
                          {item.waivedBy ? ` · ${item.waivedBy}` : ''}
                        </p>
                        {item.waivedReason && (
                          <p className="text-[11px] text-gray-500 text-left max-w-[220px] whitespace-normal">
                            {item.waivedReason}
                          </p>
                        )}
                      </>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="px-2 py-3">
                    {item.files.map((file) => (
                      <p
                        className="flex hover:underline items-center rounded text-shade-blue cursor-pointer"
                        onClick={() => openS3Document(file.fileKey)}
                      >
                        <span className="p-1">
                          <ArrowDownTrayIcon className="w-4 h-4" />
                        </span>
                        {file.name}
                      </p>
                    ))}
                  </td>
                  {!isShared && (
                    <td className="px-5 py-3">
                      <div className="flex gap-1">
                        {/* A waived charge has no balance left to edit - its amounts are zeroed by
                            design - so editing it is offered only once the waiver is reversed. */}
                        {!item.waived && (
                          <span
                            className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
                            onClick={() => onEdit(item.id, index)}
                            // Every action in this column names itself on hover. They are four
                            // unlabelled icons otherwise, and a servicer who could not tell which
                            // one waives reported that the platform had no way to waive a charge.
                            title="Edit this charge"
                          >
                            <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
                          </span>
                        )}
                        {/* Reversing and waiving are both offered on a partially waived charge:
                            there is still a balance to waive more of, and a waiver to undo. */}
                        {isChargeWaived(item) && (
                          <span
                            className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
                            onClick={() => onUnwaive(item)}
                            title="Reverse the waiver"
                          >
                            <ArrowUturnLeftIcon className="w-4 h-4"></ArrowUturnLeftIcon>
                          </span>
                        )}
                        {canWaiveCharge(item) && (
                          <span
                            className="text-amber-700 cursor-pointer hover-shadow1 p-1 rounded"
                            onClick={() => setWaivingItem(item)}
                            title="Waive this charge"
                          >
                            <NoSymbolIcon className="w-4 h-4"></NoSymbolIcon>
                          </span>
                        )}
                        <span
                          className="text-red-800 cursor-pointer hover-shadow1 p-1 rounded"
                          onClick={() => onDelete(item.id, index)}
                          title="Delete this charge (waive it instead to keep the record)"
                        >
                          <TrashIcon className="w-4 h-4"></TrashIcon>
                        </span>
                      </div>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
          {!!data.length && totals && (
            <TotalsRow
              sticky
              tdClass="px-2 py-3"
              cells={[
                { value: 'Totals', className: 'px-6 py-3 whitespace-nowrap' },
                ...(!isLoan ? [''] : []),
                '',
                '',
                '',
                '',
                '',
                '',
                totals.originalBalance,
                totals.unpaidBalance,
                totals.accruedInterest,
                totals.totalDue,
                // Waived, then Files. Not totalled: a waiver is not part of what the loan owes, and
                // the server's totals cover the collectible columns only.
                '',
                '',
                ...(!isShared ? [''] : []),
              ]}
            />
          )}
        </table>
      </div>
      {!isLoan && view !== SUMMARY && (
        <div className="flex justify-end items-center mb-6">
          <Pagination
            totalCount={totalCount}
            itemCountPerPage={itemCountPerPage}
            onNavigate={onPageNavigate}
            pageNum={filters.pageNum}
          />
        </div>
      )}
      {!isShared && isOpen && (
        <AddLoanCharge
          onUpdate={onSubmit}
          onClose={onCloseModal}
          item={editingData}
          index={selectedIndex}
          owedBy={owedBy}
          // The edit form closes behind the waive dialog rather than stacking under it: they are
          // two ways of acting on the same charge, and what the waiver does to the balances would
          // leave the form holding figures that are no longer true.
          onWaive={(charge) => {
            onCloseModal(false)
            setWaivingItem(charge)
          }}
        />
      )}
      {!isShared && waivingItem && (
        <WaiveLoanCharge
          item={waivingItem}
          onClose={() => setWaivingItem(null)}
          onSubmit={(data) => onWaiveSubmit(waivingItem, data)}
        />
      )}
    </div>
  )
}
